"use client";

import React, { useState, useEffect } from "react";
import { Modal, Switch, Typography, Space, Button, Divider, theme } from "antd";
import { 
  LOG_CATEGORIES, 
  LogCategory, 
  getLogSettings, 
  setLogSettings, 
  enableAllLogs, 
  disableAllLogs 
} from "../../utils/logger";

const { Text, Title } = Typography;

interface DevLogSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export const DevLogSettingsModal: React.FC<DevLogSettingsModalProps> = ({ open, onClose }) => {
  const { token } = theme.useToken();
  const [settings, setLocalSettings] = useState<Record<LogCategory, boolean>>(getLogSettings);

  // Refresh settings when modal opens
  useEffect(() => {
    if (open) {
      setLocalSettings(getLogSettings());
    }
  }, [open]);

  const handleToggle = (category: LogCategory, enabled: boolean) => {
    const newSettings = { ...settings, [category]: enabled };
    setLocalSettings(newSettings);
    setLogSettings(newSettings);
  };

  const handleEnableAll = () => {
    enableAllLogs();
    setLocalSettings(getLogSettings());
  };

  const handleDisableAll = () => {
    disableAllLogs();
    setLocalSettings(getLogSettings());
  };

  const enabledCount = Object.values(settings).filter(Boolean).length;
  const totalCount = Object.keys(settings).length;

  // Group categories by type for better organization
  const categoryGroups = {
    "Data & State": ['dataManager', 'subscriptions', 'api', 'graphql'] as LogCategory[],
    "Pages": ['dashboard', 'calendar', 'lessons', 'profile'] as LogCategory[],
    "Entities": ['students', 'families', 'locations', 'invoices'] as LogCategory[],
    "Other": ['auth', 'cancellation', 'subscription-billing', 'build', 'navigation'] as LogCategory[],
  };

  return (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: "18px" }}>🔧</span>
          <span>Developer Log Settings</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
    >
      <div style={{ marginBottom: "16px" }}>
        <Text type="secondary">
          Enable or disable console logs per feature. Settings are persisted in browser storage.
        </Text>
      </div>

      {/* Quick actions */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "16px",
        padding: "12px",
        background: token.colorFillQuaternary,
        borderRadius: "8px"
      }}>
        <Text>
          <strong>{enabledCount}</strong> of <strong>{totalCount}</strong> categories enabled
        </Text>
        <Space>
          <Button size="small" onClick={handleEnableAll}>
            Enable All
          </Button>
          <Button size="small" onClick={handleDisableAll}>
            Disable All
          </Button>
        </Space>
      </div>

      {/* Category groups */}
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {Object.entries(categoryGroups).map(([groupName, categories]) => (
          <div key={groupName} style={{ marginBottom: "16px" }}>
            <Text 
              strong 
              style={{ 
                fontSize: "12px", 
                textTransform: "uppercase", 
                color: token.colorTextSecondary,
                letterSpacing: "0.5px"
              }}
            >
              {groupName}
            </Text>
            <div style={{ 
              marginTop: "8px", 
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: "8px",
              overflow: "hidden"
            }}>
              {categories.map((category, index) => {
                const config = LOG_CATEGORIES[category];
                if (!config) return null;
                
                return (
                  <div 
                    key={category}
                    style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center",
                      padding: "10px 12px",
                      borderBottom: index < categories.length - 1 ? `1px solid ${token.colorBorderSecondary}` : "none",
                      background: settings[category] ? token.colorPrimaryBg : "transparent",
                      transition: "background 0.2s"
                    }}
                  >
                    <Space>
                      <span style={{ fontSize: "16px" }}>{config.emoji}</span>
                      <div>
                        <Text strong style={{ fontSize: "13px" }}>{config.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: "11px" }}>{config.description}</Text>
                      </div>
                    </Space>
                    <Switch
                      size="small"
                      checked={settings[category]}
                      onChange={(checked) => handleToggle(category, checked)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Divider style={{ margin: "16px 0" }} />

      {/* Footer info */}
      <div style={{ 
        padding: "8px 12px", 
        background: token.colorInfoBg, 
        borderRadius: "6px",
        fontSize: "12px"
      }}>
        <Text type="secondary">
          💡 <strong>Tip:</strong> Errors and warnings always show regardless of settings. 
          Changes take effect immediately.
        </Text>
      </div>
    </Modal>
  );
};

export default DevLogSettingsModal;
